import { Helmet } from "react-helmet-async";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  SITE_URL,
  metaForPath,
  jsonLdForPath,
} from "@/lib/seo";
import { trackPageView } from "@/lib/googleTracking";
import { PRODUCT } from "@/data/content";

function upsertMeta(selector, attr, value) {
  if (typeof document === "undefined" || !value) return;
  document.querySelectorAll(selector).forEach((el) => {
    el.setAttribute(attr, value);
  });
}

// Drives per-route <title>, meta description, canonical, robots, and JSON-LD.
// Mounted once inside Layout so it covers all public + diagnostic routes.
export default function RouteSeo() {
  const { pathname } = useLocation();
  const meta = metaForPath(pathname);
  const canonical = `${SITE_URL}${pathname === "/" ? "" : pathname}`;
  const jsonLd = jsonLdForPath(pathname, PRODUCT.price);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = meta.title;
    upsertMeta('meta[name="description"]', "content", meta.description);
    upsertMeta('link[rel="canonical"]', "href", canonical);
    upsertMeta('meta[property="og:title"]', "content", meta.title);
    upsertMeta('meta[property="og:description"]', "content", meta.description);
    upsertMeta('meta[property="og:url"]', "content", canonical);
    upsertMeta('meta[name="twitter:title"]', "content", meta.title);
    upsertMeta('meta[name="twitter:description"]', "content", meta.description);
    trackPageView(pathname, meta.title);
  }, [canonical, meta.description, meta.title, pathname]);

  return (
    <Helmet prioritizeSeoTags>
      <title>{meta.title}</title>
      {meta.description ? (
        <meta name="description" content={meta.description} />
      ) : null}
      <link rel="canonical" href={canonical} />
      {meta.noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow" />
      )}
      {/* Per-route OG/Twitter overrides (default card is in index.html) */}
      <meta property="og:title" content={meta.title} />
      {meta.description ? (
        <meta property="og:description" content={meta.description} />
      ) : null}
      <meta property="og:url" content={canonical} />
      <meta name="twitter:title" content={meta.title} />
      {meta.description ? (
        <meta name="twitter:description" content={meta.description} />
      ) : null}
      {jsonLd.map((obj, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
